import { useEffect, useRef, useState, forwardRef, useCallback } from "react";
import { useStateValue } from "../context/state/provider";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Message from "../components/Message";
import MessageLoader from "../components/MessageLoader";
import MessageInput from "../components/MessageInput";
import Loader from "react-loader-spinner";
import ErrorIcon from "@material-ui/icons/Error";
import { useDocumentData } from "react-firebase-hooks/firestore";

const useStyles = makeStyles({
	chatDisplay: {
		overflowY: "auto",
		overflowX: "hidden",
		animation: "fadeInAnimation 0.5s ease",
		maxHeight: "calc(100% - 59px)",
		paddingRight: "8px",
		"&:focus": {
			outline: "2px solid var(--active-brand-color)",
			outlineOffset: "-2px",
		}
	},
	loadingContainer: {
		marginTop: "100px",
		color: "var(--color-dark-grey)",
		fontSize: "18px",
		animation: "fadeInAnimation 0.1s linear",
	},
	errorBox: {
		borderRadius: "10px",
		padding: "15px",
		backgroundColor: "var(--bot-msg-bg)",
		marginLeft: "42px",
	},
	"@media (max-width: 375px)": {
		chatDisplay: {
			padding: "8px 8px 0px 8px",
		},
	},
});

const ChatPanel = forwardRef(({
	messages,
	messagesRef,
	firestore,
	fsLoading,
	fsError,
	online,
	userLoading,
	disableBtn,
	setErrMsg,
	setShowErrorMessage,
	justOpen,
	handleJustOpen,
	chatbotIsDraft,
	user,
	userLoadingAU,
	chatbotId,
	avatarUrl,
	handleFallbackMessage
}, ref) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [{ loaded, admin, loggingOut }, dispatch] = useStateValue(); //user
	const messagesEndRef = useRef(null);
	const chatPanelRef = useRef(null);
	const [submittingMsg, setSubmittingMsg] = useState(false);
	const [justFlag, setJustFlag] = useState(false)

	// Add these for screen reader announcements
	const [lastMessageId, setLastMessageId] = useState("");
	const [isStreaming, setIsStreaming] = useState(false);
	const [completeMessage, setCompleteMessage] = useState("");
	const announcementRef = useRef(null);
	const streamTimeoutRef = useRef(null);
	const [lastStreamUpdate, setLastStreamUpdate] = useState(0);
	const STREAM_IDLE_THRESHOLD = 2000; // 2 seconds without updates means streaming is complete


	function handleFlag() {
		setJustFlag(true)
	}

	function handleSubmit() {
		setSubmittingMsg(true)
		setJustFlag(false)
	}

	// Add this effect to announce new messages to screen readers
	// Function to announce a message immediately
	const announceMessage = useCallback((msg, prefix = "") => {
		if (!msg.data || !msg.data.text) return;

		const messageText = Array.isArray(msg.data.text)
			? msg.data.text[0].replace(/<[^>]*>/g, '')
			: msg.data.text.replace(/<[^>]*>/g, '');

		if (announcementRef.current) {
			announcementRef.current.textContent = "";
			void announcementRef.current.offsetWidth;
			announcementRef.current.textContent = prefix + messageText;
		}
	}, []);

	// Function to announce the complete message after streaming
	const announceCompleteMessage = useCallback((prefix = "") => {
		if (announcementRef.current && completeMessage) {
			announcementRef.current.textContent = "";
			void announcementRef.current.offsetWidth;
			announcementRef.current.textContent = prefix + completeMessage;

			// Reset the complete message
			setCompleteMessage("");
		}
	}, [completeMessage]);

	// Add this effect to detect new messages and announce them
	useEffect(() => {
		if (messages && messages.length > 0) {
			const lastMsg = messages[messages.length - 1];
			
			// Skip if this isn't a new message
			if (!lastMsg || lastMsg.id === lastMessageId) return;
			
			// Update the last message ID
			setLastMessageId(lastMsg.id);
			
			// Announce the message with appropriate prefix
			if (lastMsg.data.uid === user?.uid + "***" + chatbotId) {
				announceMessage(lastMsg, "You said: ");
			} else {
				const sender = lastMsg.data.uid === "Live Agent" 
					? "Live Agent says: " 
					: (lastMsg.data.chatbotName || "Agent") + " says: ";
				
				announceMessage(lastMsg, sender);
			}
		}
	}, [messages, lastMessageId, user, chatbotId, announceMessage]);

	// toggle on chatPanel render to force scroll when opening.
	useEffect(() => {
		dispatch({
			type: "CHAT_PANEL_STATE",
			loaded: true,
		});

		return () => {
			dispatch({
				type: "CHAT_PANEL_STATE",
				loaded: false,
			});
		};
	}, [dispatch]);

	const roomRef =
		!userLoadingAU && !fsError && user
			? firestore.collection("rooms").doc(user.uid + "***" + chatbotId)
			: "";

	const [roomValue] = useDocumentData(roomRef);
	// Manage message loader on pendingMessages change
	useEffect(() => {
		roomValue && roomValue.pendingMessages > 0
			? setLoading(true)
			: setLoading(false);
	}, [roomValue]);

	//to reset loader after the network disconnect
	useEffect(() => {
		if (!online) {
			setLoading(false);
		}
	}, [online]);

	useEffect(() => {
		if (!justFlag && messagesEndRef.current) {
		}
		else if (!justFlag && !loading) {
			setSubmittingMsg(false)
		}
	}, [loading, loaded, roomRef, justFlag]);

	//********************************************
	const scrollToBottom = () => {
		chatPanelRef.current.scrollTop = chatPanelRef.current.scrollHeight;
	};

	const handleImageLoad = () => {
		if (!justFlag && !loading) {
			scrollToBottom(); // Scroll to bottom only if not flagged and not loading
		}
		setSubmittingMsg(false);
	};
	//********************************************

	useEffect(() => {
		if (messages) {
		}

		if (chatPanelRef.current) {
			chatPanelRef.current.scrollTop =
				chatPanelRef.current.scrollHeight;
		}
	}, [messages, loading
	]);

	const showAvatar = (currentUid, currentIndex) => {
		const nextMsg = messages[currentIndex + 1];
		if (!nextMsg || nextMsg.data.uid === currentUid) {
			return false;
		}
		return true;
	};

	const showDate = (time, currentIndex) => {
		const nextMsg = messages[currentIndex + 1] || null;
		const currentMsgDay = time.toDate().toDateString();
		const nextMsgDay = nextMsg
			? nextMsg.data.createdAt.toDate().toDateString()
			: null;

		const dates = {};
		const today = new Date();

		if (nextMsgDay && currentMsgDay !== nextMsgDay) {
			//add next message date
			dates.nextDate = nextMsgDay;
		}
		if (currentIndex === 0 && currentMsgDay !== today.toDateString()) {
			// return prev and previous date
			dates.prevDate = currentMsgDay;
		}
		return dates;
	};

	return (
		<>
			{/* Add this dedicated screen reader announcement area */}
			{/* <div
				ref={announcementRef}
				aria-live="assertive"
				aria-atomic="true"
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					margin: '-1px',
					padding: 0,
					overflow: 'hidden',
					clip: 'rect(0, 0, 0, 0)',
					whiteSpace: 'nowrap',
					borderWidth: 0
				}}
			>
				// {/* Initial content 
				Chat loaded
			</div> */}
			<div
				className={classes.chatDisplay}
				ref={chatPanelRef}
				aria-label="Chat conversation"
				aria-live="polite"
				aria-atomic="false"
				aria-relevant="additions"
				role="log"
				tabIndex="0"  // Add this line to make the container focusable
			>
				{!userLoading &&
					messages &&
					messages.length &&
					!fsLoading &&
					!fsError ? (
					<>
						{messages.map((msg, i) => (
							<Message
								handleFlag={handleFlag}
								key={msg.id}
								messageId={msg.id}
								adminStatus={admin}
								flagged={msg.data.adminFlag}
								lastMessage={messages.length === i + 1}
								showUser={showAvatar(msg.data.uid, i)}
								showDate={showDate(msg.data.createdAt, i)}
								options={msg.data.options}
								multipleAnswers={msg.data.multipleAnswers}
								user={user}
								uid={msg.data.uid}
								uidDefinition={msg.data.uidDefinition}
								avatar={msg.data.photoUrl}
								message={msg}
								time={msg.data.createdAt.toDate()}
								messagesRef={messagesRef}
								online={online}
								messageQty={messages.length}
								chatbotIsDraft={chatbotIsDraft}
								onImageLoad={handleImageLoad}
								chatbotId={chatbotId}
								requestCancelled={msg.data.requestCancelled}
								setLoading={setLoading}
								handleFallbackMessage={handleFallbackMessage}
								avatarUrl={avatarUrl}
							/>
						))}
						{loading && online && <MessageLoader />}
						{!online && (
							<Box m={2} className={classes.errorBox} role="alert">
								<Box>
									<ErrorIcon color="error" />
								</Box>
								<Typography>
									Network Error.
									<br />
									Connection required to send and receive
									messages.
								</Typography>
							</Box>
						)}
						{chatbotIsDraft && (
							<Box m={2} className={classes.errorBox} role="alert">
								<Box>
									<ErrorIcon color="error" />
								</Box>
								<Typography>
									The agent is currently in draft mode.
								</Typography>
							</Box>
						)}
						{<div ref={messagesEndRef} />}
					</>
				) : (
					<>
						{(!fsError || userLoading) && (
							<div 
								className={classes.loadingContainer} 
								role="status" 
								aria-label="Loading messages"
							>
								Loading Messages
								<br />
								<br />
								<Loader
									type="Oval"
									color="var(--active-brand-color)"
									height={120}
									width={75}
									aria-hidden="true"
								></Loader>
							</div>
						)}
						{fsError && !userLoading && (
							<Box
								className={classes.errorBox}
								m={7}
								display="flex"
								justifyContent="center"
								alignItems="center"
								role="alert"
							>
								<Typography variant="h7">
									<ErrorIcon color="error" />
									<br />
									An error occured. <br />
									<br /> Please try refreshing the page.{" "}
									<br />
								</Typography>
							</Box>
						)}{" "}
					</>
				)}
			</div>
			<div style={{ display: "flex", flex: "1 1 auto" }}></div>
			{!loggingOut && <MessageInput
				online={online}
				messagesRef={messagesRef}
				firestore={firestore}
				setErrMsg={setErrMsg}
				setShowErrorMessage={setShowErrorMessage}
				disableBtn={disableBtn}
				messages={messages?.length}
				justFlag={justFlag}
				handleSubmit={handleSubmit}
				justOpen={justOpen}
				handleJustOpen={handleJustOpen}
				setLoading={setLoading}
				chatbotIsDraft={chatbotIsDraft}
				chatbotId={chatbotId}
				handleFallbackMessage={handleFallbackMessage}
			/>
			}
		</>
	);
})

export default ChatPanel;
